import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { Plus, Send, FileText, AlertCircle, CheckCircle2, Clock } from "lucide-react";

function StatusBadge({ status }) {
  const map = {
    draft: ["badge-draft", FileText, "Draft"],
    sending: ["badge-sending", Send, "Sending"],
    completed: ["badge-completed", CheckCircle2, "Completed"],
    failed: ["badge-failed", AlertCircle, "Failed"],
  };
  const [cls, Icon, label] = map[status] || map.draft;
  return <span className={`badge ${cls}`}><Icon size={11} /> {label}</span>;
}

export default function Campaigns() {
  const nav = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState({ active: 0, total: 0, unsubscribed: 0, bounced: 0 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const refresh = async () => {
    try {
      const [c, s] = await Promise.all([
        api.get("/campaigns"),
        api.get("/recipients/stats"),
      ]);
      setCampaigns(c.data);
      setStats(s.data);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const createNew = async () => {
    try {
      // Create empty — the editor will show the block-based template picker
      // (including "Assam Flood Relief Appeal 2026" and the other starters).
      const { data } = await api.post("/campaigns", {
        name: `RAHI-feed · ${new Date().toLocaleDateString("en-IN", { month: "short", year: "numeric" })}`,
        subject: "",
        html: "",
      });
      nav(`/campaigns/${data.id}`);
    } catch (e) {
      alert(formatErr(e.response?.data?.detail));
    }
  };

  return (
    <div className="px-12 py-10 max-w-[1200px]" data-testid="campaigns-page">
      {/* Header */}
      <div className="flex items-end justify-between mb-12">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] mb-2" style={{ color: "var(--rahi-red)" }}>Mission Control</p>
          <h1 className="font-display text-5xl font-black tracking-tight leading-none">Campaigns</h1>
          <p className="mt-3 text-sm" style={{ color: "var(--rahi-muted)" }}>
            Compose, preview and send your quarterly newsletter to the RAHI community.
          </p>
        </div>
        <button onClick={createNew} className="btn btn-primary" data-testid="new-campaign-btn">
          <Plus size={14} /> New campaign
        </button>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-4 gap-4 mb-12">
        <StatTile label="Active recipients" value={stats.active} testid="stat-active" />
        <StatTile label="Total in list" value={stats.total} testid="stat-total" />
        <StatTile label="Unsubscribed" value={stats.unsubscribed} testid="stat-unsubscribed" />
        <StatTile label="Bounced" value={stats.bounced} testid="stat-bounced" />
      </div>

      {err && <div className="text-sm mb-4 p-3 rounded-md" style={{ background:"#FEF2F2", color:"#B91C1C" }}>{err}</div>}

      {loading ? (
        <div className="text-sm" style={{ color: "var(--rahi-soft)" }}>Loading campaigns…</div>
      ) : campaigns.length === 0 ? (
        <div className="card p-16 text-center">
          <div className="font-display text-2xl font-black mb-2">No campaigns yet</div>
          <p className="text-sm mb-6" style={{ color: "var(--rahi-muted)" }}>
            Start with your refined RAHI-feed template and send your first quarterly update.
          </p>
          <button onClick={createNew} className="btn btn-primary mx-auto" data-testid="empty-state-create-btn">
            <Plus size={14} /> Create first campaign
          </button>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm" data-testid="campaigns-table">
            <thead>
              <tr style={{ background:"#FBFAF9", color:"var(--rahi-muted)" }} className="text-left text-[11px] uppercase tracking-[0.1em]">
                <th className="px-6 py-3 font-medium">Campaign</th>
                <th className="px-6 py-3 font-medium">Status</th>
                <th className="px-6 py-3 font-medium text-right">Sent</th>
                <th className="px-6 py-3 font-medium text-right">Opens</th>
                <th className="px-6 py-3 font-medium text-right">Clicks</th>
                <th className="px-6 py-3 font-medium">Created</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => nav(`/campaigns/${c.id}`)}
                  className="border-t cursor-pointer hover:bg-[#FBFAF9] transition-colors"
                  style={{ borderColor: "var(--rahi-border)" }}
                  data-testid="campaign-row"
                >
                  <td className="px-6 py-4">
                    <div className="font-medium">{c.name}</div>
                    <div className="text-xs mt-0.5" style={{ color: "var(--rahi-soft)" }}>{c.subject}</div>
                  </td>
                  <td className="px-6 py-4"><StatusBadge status={c.status} /></td>
                  <td className="px-6 py-4 text-right font-mono text-xs">
                    {c.sent_count}{c.total_recipients ? ` / ${c.total_recipients}` : ""}
                  </td>
                  <td className="px-6 py-4 text-right font-mono text-xs">{c.opened_count}</td>
                  <td className="px-6 py-4 text-right font-mono text-xs">{c.clicked_count}</td>
                  <td className="px-6 py-4 text-xs" style={{ color: "var(--rahi-muted)" }}>
                    {new Date(c.created_at).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatTile({ label, value, testid }) {
  return (
    <div className="card p-5" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-[0.12em] mb-2" style={{ color: "var(--rahi-soft)" }}>{label}</div>
      <div className="font-display text-3xl font-black tracking-tight">{value.toLocaleString("en-IN")}</div>
    </div>
  );
}
